<template>
  <v-row justify="center">
    <v-col xl="10">
      <v-card>
        <v-card-title>
          <h2>{{ $t("admin.users.title") }}</h2>
          <v-dialog v-model="createDialog" max-width="600px">
            <template v-slot:activator="{ on }">
              <v-btn text :icon="$vuetify.breakpoint.smAndDown" color="primary" v-on="on" class="ml-5">
                <v-icon left>mdi-account-plus</v-icon>
                <span class="hidden-sm-and-down">{{ $t("admin.users.actions.add") }}</span>
              </v-btn>
            </template>
            <v-card>
              <v-form @submit.prevent="createUser" ref="createForm" v-model="valid">
                <v-card-title>
                  <span class="headline">{{ $t("admin.users.create.title") }}</span>
                </v-card-title>

                <v-card-text>
                  <v-text-field
                    v-model="newUserDisplayName"
                    :label="$t('admin.users.create.displayName')"
                    name="newUserDisplayName"
                    :rules="[$v.required()]"
                    autocomplete="off"
                    required
                  ></v-text-field>
                  <v-text-field
                    v-model="newUserUsername"
                    :label="$t('admin.users.create.username')"
                    name="newUserUsername"
                    :rules="[$v.required(), $v.isEmail()]"
                    autocomplete="off"
                    required
                  ></v-text-field>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" type="submit" :disabled="!valid">
                    {{ $t("admin.users.create.submit") }}
                  </v-btn>
                  <v-btn color="secondary" text @click="createDialog = false">
                    {{ $t("admin.users.create.cancel") }}
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('admin.users.actions.search')"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalItems"
          :loading="loading"
          item-key="id"
          class="elevation-1"
        >
          <template v-slot:item.displayName="props">
            <v-edit-dialog
              :return-value.sync="props.item.displayName"
              lazy
              @save="save(props.item)"
              large
              :cancel-text="$t('admin.users.edit.cancel')"
              :save-text="$t('admin.users.edit.save')"
            >
              <span>
                {{ props.item.displayName }}
                <v-icon small absolute right class="show-only-on-hover">mdi-pencil</v-icon>
              </span>
              <template v-slot:input>
                <v-text-field
                  v-model="props.item.displayName"
                  :label="$t('admin.users.list.displayName')"
                  single-line
                  autofocus
                  required
                ></v-text-field>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.username="props">
            {{ props.item.username }}
          </template>
          <template v-slot:item.password="props">
            <v-btn raised text :icon="$vuetify.breakpoint.smAndDown" color="primary" @click="resetPassword(props.item)">
              <v-icon left>mdi-lock-reset</v-icon>
              <span class="hidden-sm-and-down">{{ $t("admin.users.actions.passwordReset") }}</span>
            </v-btn>
            <v-btn raised text :icon="$vuetify.breakpoint.smAndDown" color="error" @click="confirmDelete = props.item">
              <v-icon left>mdi-delete</v-icon>
              <span class="hidden-sm-and-down">{{ $t("admin.users.actions.remove") }}</span>
            </v-btn>
          </template>
        </v-data-table>
        <v-dialog v-model="deleteDialog" max-width="500">
          <v-card>
            <v-card-title class="headline">
              {{ $t("admin.users.remove.title", [confirmDelete && confirmDelete.displayName]) }}
            </v-card-title>

            <v-card-text>
              {{ $t("admin.users.remove.message") }}
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="permanentDelete(confirmDelete)">
                {{ $t("admin.users.remove.confirm") }}
              </v-btn>
              <v-btn color="secondary" text @click="confirmDelete = null">
                {{ $t("admin.users.remove.cancel") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="resetPasswordDialog" max-width="500">
          <v-card>
            <v-card-title class="headline">
              {{ $t("admin.users.resetPassword.title") }}
            </v-card-title>

            <v-card-text>
              <v-text-field
                readonly
                v-model="resetPasswordLink"
                append-outer-icon="mdi-content-copy"
                @click="copyToClipboard"
                @click:append-outer="copyToClipboard"
                ref="resetPasswordLink"
                :success-messages="copiedMessage"
              />
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="secondary"
                text
                @click="
                  resetPasswordLink = null;
                  copiedMessage = [];
                "
              >
                {{ $t("admin.users.resetPassword.close") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from "axios";
import debounce from "../../../util/debounce";

export default {
  data() {
    return {
      valid: false,
      totalItems: 0,
      items: [],
      loading: true,
      confirmDelete: null,
      createDialog: false,
      newUserDisplayName: null,
      newUserUsername: null,
      resetPasswordLink: null,
      copiedMessage: [],
      pagination: {
        itemsPerPage: 25,
      },
      search: "",
      headers: [
        { text: this.$t("admin.users.list.displayName"), value: "displayName" },
        { text: this.$t("admin.users.list.username"), value: "username" },
        {
          text: this.$t("admin.users.list.password"),
          value: "password",
          sortable: false,
        },
      ],
    };
  },
  mounted() {
    this.getDataFromApi();
  },
  computed: {
    deleteDialog() {
      return !!this.confirmDelete;
    },
    resetPasswordDialog() {
      return !!this.resetPasswordLink;
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getDataFromApi();
      },
      deep: true,
    },
    search: {
      handler() {
        debounce(this.getDataFromApi);
      },
      deep: true,
    },
  },
  methods: {
    async save(user) {
      await axios.put(`/admin/users/${user.id}`, user);
    },
    async createUser() {
      if (!this.$data.valid) return;
      await axios.post("/admin/users", {
        username: this.newUserUsername,
        displayName: this.newUserDisplayName,
        appAdmin: true,
      });
      this.createDialog = false;
      this.getDataFromApi();
    },
    copyToClipboard() {
      this.$refs.resetPasswordLink.focus();
      this.$refs.resetPasswordLink.$refs.input.select();
      document.execCommand("selectAll");
      if (document.execCommand("copy")) {
        this.copiedMessage = [this.$t("admin.users.resetPassword.copied")];
      }
    },
    async permanentDelete(user) {
      await axios.delete(`/admin/users/${user.id}`);
      this.confirmDelete = null;
      this.getDataFromApi();
    },
    async resetPassword(user) {
      let result = await axios.patch(`/admin/users/${user.id}/resetpassword`);
      if (result && result.resetPasswordToken) {
        this.resetPasswordLink = `${location.origin}/user/resetpassword/${result.resetPasswordToken}`;
      }
    },
    async getDataFromApi() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
      this.loading = true;
      const result = await axios.get("/admin/users", {
        params: {
          page: page - 1,
          size: itemsPerPage,
          sortBy: sortBy.join(","),
          sortOrder: sortDesc.map((s) => (s ? "desc" : "asc")).join(","),
          search: this.search || undefined,
          eventId: null,
        },
      });
      this.items = result.rows;
      this.totalItems = result.count;
      this.loading = false;
    },
  },
};
</script>

<style></style>
